import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Save, Settings2 } from "lucide-react";
import { axiosInstance } from "../lib/axios";

// Bidding Configuration (Admin) — the bidding module is configuration-driven:
// Freelance Platforms, Freelance Accounts (platform + assigned BD + connect
// rate + status) and the Job Type / Bid Status / Boost Type option lists are
// all managed here instead of being hardcoded.

type Tab = "platforms" | "accounts" | "options";

// Profile pictures bundled with the app (public/upwork-account-profiles).
// Picking one here is all that is needed — every client-facing surface then
// fetches it through the freelance account.
const PROFILE_PICS = [
  { value: "", label: "— No picture —" },
  { value: "/upwork-account-profiles/irfan.jpg", label: "Irfan" },
  { value: "/upwork-account-profiles/nadeem.webp", label: "Nadeem" },
  { value: "/upwork-account-profiles/farah.webp", label: "Farah" },
];

const inputCls =
  "px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40";

const AdminBiddingConfigPage = () => {
  const [tab, setTab] = useState<Tab>("platforms");
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [bds, setBds] = useState<any[]>([]);
  const [config, setConfig] = useState<any>({ jobTypes: [], bidStatuses: [], boostTypes: [] });
  const [newPlatform, setNewPlatform] = useState({ name: "", connectRate: "" });
  const [newAccount, setNewAccount] = useState({ name: "", platform: "", assignedBd: "", connectRate: "", totalConnects: "" });
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [p, a, c] = await Promise.all([
        axiosInstance.get("/bids/platforms"),
        axiosInstance.get("/bids/accounts"),
        axiosInstance.get("/bids/config"),
      ]);
      setPlatforms(p.data || []);
      setAccounts(a.data || []);
      setConfig(c.data || { jobTypes: [], bidStatuses: [], boostTypes: [] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load bidding configuration");
    }
    try {
      const { data } = await axiosInstance.get("/messages/users");
      setBds((data || []).filter((u: any) => ["Business Developer", "Admin"].includes(u.role)));
    } catch { /* BD list optional */ }
  };
  useEffect(() => { loadAll(); }, []);

  const addPlatform = async () => {
    if (!newPlatform.name.trim()) return toast.error("Platform name is required");
    setBusy(true);
    try {
      await axiosInstance.post("/bids/platforms", {
        name: newPlatform.name.trim(),
        connectRate: Number(newPlatform.connectRate) || 0,
      });
      setNewPlatform({ name: "", connectRate: "" });
      toast.success("Platform added");
      loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to add platform");
    } finally { setBusy(false); }
  };

  const patchPlatform = async (id: string, patch: any) => {
    try {
      await axiosInstance.patch(`/bids/platforms/${id}`, patch);
      loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update platform");
    }
  };

  const addAccount = async () => {
    if (!newAccount.name.trim()) return toast.error("Account name is required");
    setBusy(true);
    try {
      await axiosInstance.post("/bids/accounts", {
        name: newAccount.name.trim(),
        platform: newAccount.platform || undefined,
        assignedBd: newAccount.assignedBd || undefined,
        connectRate: newAccount.connectRate === "" ? "" : Number(newAccount.connectRate),
        totalConnects: Number(newAccount.totalConnects) || 0,
      });
      setNewAccount({ name: "", platform: "", assignedBd: "", connectRate: "", totalConnects: "" });
      toast.success("Account added");
      loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to add account");
    } finally { setBusy(false); }
  };

  const patchAccount = async (id: string, patch: any) => {
    try {
      await axiosInstance.patch(`/bids/accounts/${id}`, patch);
      loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update account");
    }
  };

  // Option lists (job types / statuses / boost types) — edited locally, saved together.
  const setOption = (key: string, idx: number, patch: any) =>
    setConfig((c: any) => ({
      ...c,
      [key]: c[key].map((o: any, i: number) => (i === idx ? { ...o, ...patch } : o)),
    }));
  const addOption = (key: string) =>
    setConfig((c: any) => ({ ...c, [key]: [...c[key], { name: "", isActive: true }] }));
  const saveOptions = async () => {
    setBusy(true);
    try {
      const clean = (l: any[]) => l.filter((o) => String(o.name || "").trim());
      const { data } = await axiosInstance.put("/bids/config", {
        jobTypes: clean(config.jobTypes),
        bidStatuses: clean(config.bidStatuses),
        boostTypes: clean(config.boostTypes),
      });
      setConfig(data);
      toast.success("Bidding options saved");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to save options");
    } finally { setBusy(false); }
  };

  const OptionList = ({ title, k, note }: { title: string; k: string; note?: string }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-800">{title}</h4>
        <button onClick={() => addOption(k)} className="text-[#0058c3] text-xs font-semibold hover:underline inline-flex items-center gap-1">
          <Plus size={13} /> Add
        </button>
      </div>
      {note && <p className="text-[11px] text-slate-400">{note}</p>}
      {(config[k] || []).map((o: any, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <input
            className={`${inputCls} flex-1`}
            value={o.name}
            onChange={(e) => setOption(k, i, { name: e.target.value })}
            placeholder="Option name"
          />
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600 shrink-0">
            <input type="checkbox" checked={o.isActive !== false} onChange={(e) => setOption(k, i, { isActive: e.target.checked })} />
            Active
          </label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-[26px] font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Settings2 size={24} className="text-[#0058c3]" /> Bidding Configuration
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Platforms, freelance accounts, job types, statuses and boost types — configuration-driven, no code changes needed.
          </p>
        </div>

        <div className="flex gap-2">
          {([
            ["platforms", "Freelance Platforms"],
            ["accounts", "Freelance Accounts"],
            ["options", "Job Types · Statuses · Boost"],
          ] as [Tab, string][]).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                tab === t ? "bg-[#0058c3] text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "platforms" && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Platform name</label>
                <input className={inputCls} placeholder="e.g. Fiverr" value={newPlatform.name} onChange={(e) => setNewPlatform({ ...newPlatform, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Connect/credit rate ($)</label>
                <input className={inputCls} type="number" min={0} step="0.001" placeholder="0.165" value={newPlatform.connectRate} onChange={(e) => setNewPlatform({ ...newPlatform, connectRate: e.target.value })} />
              </div>
              <button onClick={addPlatform} disabled={busy} className="px-4 py-2 rounded-lg bg-[#0058c3] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                <Plus size={15} /> Add Platform
              </button>
            </div>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] text-slate-400 uppercase tracking-wider">
                  <th className="py-2 pr-3">Platform</th>
                  <th className="py-2 pr-3">Connect Rate ($)</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {platforms.map((p) => (
                  <tr key={p._id}>
                    <td className="py-2.5 pr-3 font-semibold text-slate-800">{p.name}</td>
                    <td className="py-2.5 pr-3">
                      <input
                        className={`${inputCls} w-28`}
                        type="number" min={0} step="0.001"
                        defaultValue={p.connectRate ?? 0}
                        onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== p.connectRate) patchPlatform(p._id, { connectRate: v }); }}
                        title="Cost per connect/credit — used to calculate Connect Cost on bids"
                      />
                    </td>
                    <td className="py-2.5 pr-3">
                      <button
                        onClick={() => patchPlatform(p._id, { isActive: p.isActive === false })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${p.isActive !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                      >
                        {p.isActive !== false ? "Active" : "Inactive"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "accounts" && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Account name</label>
                <input className={inputCls} placeholder="e.g. Ahmed Upwork" value={newAccount.name} onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Platform</label>
                <select className={inputCls} value={newAccount.platform} onChange={(e) => setNewAccount({ ...newAccount, platform: e.target.value })}>
                  <option value="">Upwork (default)</option>
                  {platforms.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Assigned BD</label>
                <select className={inputCls} value={newAccount.assignedBd} onChange={(e) => setNewAccount({ ...newAccount, assignedBd: e.target.value })}>
                  <option value="">— None —</option>
                  {bds.map((u) => <option key={u._id} value={u._id}>{u.fullName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Rate override ($)</label>
                <input className={`${inputCls} w-24`} type="number" min={0} step="0.001" placeholder="platform" value={newAccount.connectRate} onChange={(e) => setNewAccount({ ...newAccount, connectRate: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Connects</label>
                <input className={`${inputCls} w-24`} type="number" min={0} placeholder="0" value={newAccount.totalConnects} onChange={(e) => setNewAccount({ ...newAccount, totalConnects: e.target.value })} />
              </div>
              <button onClick={addAccount} disabled={busy} className="px-4 py-2 rounded-lg bg-[#0058c3] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                <Plus size={15} /> Add Account
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[820px]">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] text-slate-400 uppercase tracking-wider">
                    <th className="py-2 pr-3">Account</th>
                    <th className="py-2 pr-3">Client-Facing Profile</th>
                    <th className="py-2 pr-3">Platform</th>
                    <th className="py-2 pr-3">Assigned BD</th>
                    <th className="py-2 pr-3">Connects (used/total)</th>
                    <th className="py-2 pr-3">Rate ($)</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accounts.map((a) => (
                    <tr key={a._id} className={a.isActive === false ? "opacity-60" : ""}>
                      <td className="py-2.5 pr-3 font-semibold text-slate-800">{a.name}</td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          {a.profilePicUrl ? (
                            <img src={a.profilePicUrl} alt={a.name} className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-[11px] font-bold text-slate-400">
                              {(a.name || "?").charAt(0)}
                            </div>
                          )}
                          <select
                            className={inputCls}
                            value={PROFILE_PICS.some((p) => p.value === (a.profilePicUrl || "")) ? (a.profilePicUrl || "") : "custom"}
                            onChange={(e) => patchAccount(a._id, { profilePicUrl: e.target.value === "custom" ? a.profilePicUrl : e.target.value })}
                            title="Picture shown to clients across the portal, inbox and proposals"
                          >
                            {PROFILE_PICS.map((p) => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                            {!PROFILE_PICS.some((p) => p.value === (a.profilePicUrl || "")) && (
                              <option value="custom">Custom URL</option>
                            )}
                          </select>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <select
                          className={inputCls}
                          value={a.platform?._id || ""}
                          onChange={(e) => patchAccount(a._id, { platform: e.target.value || null })}
                        >
                          <option value="">—</option>
                          {platforms.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td className="py-2.5 pr-3">
                        <select
                          className={inputCls}
                          value={a.assignedBd?._id || ""}
                          onChange={(e) => patchAccount(a._id, { assignedBd: e.target.value || null })}
                        >
                          <option value="">— None —</option>
                          {bds.map((u) => <option key={u._id} value={u._id}>{u.fullName}</option>)}
                        </select>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600">
                        {a.usedConnects || 0} / {a.totalConnects || 0}
                      </td>
                      <td className="py-2.5 pr-3">
                        <input
                          className={`${inputCls} w-24`}
                          type="number" min={0} step="0.001"
                          placeholder={`${a.platform?.connectRate ?? 0}`}
                          defaultValue={a.connectRate ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value;
                            patchAccount(a._id, { connectRate: v === "" ? null : Number(v) });
                          }}
                          title="Account-specific $/connect — empty inherits the platform rate"
                        />
                      </td>
                      <td className="py-2.5 pr-3">
                        <button
                          onClick={() => patchAccount(a._id, { isActive: a.isActive === false })}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${a.isActive !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                          title="Deactivated accounts can't take NEW bids — historical bids stay intact"
                        >
                          {a.isActive !== false ? "Active" : "Deactivated"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "options" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OptionList title="Job Types" k="jobTypes" note="New Job · Invitation · Directly Awarded (defaults)" />
              <OptionList title="Bid Statuses" k="bidStatuses" note="Shown in the bid status dropdowns" />
              <OptionList title="Boost Types" k="boostTypes" note="Organic · Boosted (defaults)" />
            </div>
            <button onClick={saveOptions} disabled={busy} className="px-5 py-2.5 rounded-lg bg-[#0058c3] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              <Save size={15} /> {busy ? "Saving…" : "Save Options"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBiddingConfigPage;
